import { useCallback, useEffect, useRef, useState } from 'react';
import type { Diagram, Project } from '../../store/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';

type HeaderProps = {
  project: Project | undefined;
  diagram: Diagram | undefined;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onOpenSettings: () => void;
  onRenameDiagram: () => void;
  onOpenSidebar: () => void;
  onShare: () => void;
};

function MenuIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function LogoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M3 4l9 16 9-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M7 4h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="12" cy="3" r="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="4" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="13" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.7 7l4.6-3M5.7 9l4.6 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M6.86 1.58a1.18 1.18 0 012.28 0l.17.66a1.18 1.18 0 001.58.72l.6-.31a1.18 1.18 0 011.62 1.14l-.05.68a1.18 1.18 0 001.14 1.24l.68.06a1.18 1.18 0 01.57 2.2l-.56.38a1.18 1.18 0 00-.36 1.62l.36.57a1.18 1.18 0 01-1.14 1.81l-.68-.1a1.18 1.18 0 00-1.3 1.06l-.12.67a1.18 1.18 0 01-2.24.36l-.32-.6a1.18 1.18 0 00-1.62-.42l-.57.36a1.18 1.18 0 01-1.81-1.14l.1-.68a1.18 1.18 0 00-1.06-1.3l-.67-.12a1.18 1.18 0 01-.36-2.24l.6-.32a1.18 1.18 0 00.42-1.62l-.36-.57a1.18 1.18 0 011.14-1.81l.68.1a1.18 1.18 0 001.3-1.06z" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function LogOutIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M6 2H4a2 2 0 00-2 2v8a2 2 0 002 2h2M10.5 11.5L14 8l-3.5-3.5M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Header({ project, diagram, sidebarOpen, onToggleSidebar, onOpenSettings, onRenameDiagram, onOpenSidebar, onShare }: HeaderProps) {
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = useCallback(() => setUserMenuOpen((v) => !v), []);

  // Close on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  return (
    <header className="ds-header">
      <div className="ds-header__left">
        <button
          type="button"
          className="ds-header__menu-btn"
          onClick={onToggleSidebar}
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          <MenuIcon />
        </button>
        <div className="ds-header__brand">
          <LogoIcon />
          <span>Sketchflow</span>
        </div>
      </div>

      <nav className="ds-breadcrumb" aria-label="Breadcrumb">
        <button
          type="button"
          className="ds-breadcrumb__segment"
          onClick={onOpenSidebar}
          title="Switch project"
        >
          {project?.name || 'Project'}
        </button>
        <span className="ds-breadcrumb__sep" aria-hidden="true">/</span>
        <button
          type="button"
          className="ds-breadcrumb__segment ds-breadcrumb__segment--active"
          onClick={onRenameDiagram}
          title="Click to rename"
        >
          {diagram?.title || 'Untitled Diagram'}
        </button>
      </nav>

      <div className="ds-header__right">
        {diagram && (
          <button type="button" className="ds-icon-btn" onClick={onShare} aria-label="Share diagram">
            <ShareIcon />
          </button>
        )}
        <button type="button" className="ds-icon-btn" onClick={onOpenSettings} aria-label="Settings">
          <SettingsIcon />
        </button>
        {user && (
          <div className="ds-header__user" ref={menuRef}>
            <button
              type="button"
              className="ds-header__avatar-btn"
              onClick={toggleMenu}
              aria-label="User menu"
              aria-expanded={userMenuOpen}
            >
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="ds-header__avatar" />
              ) : (
                <div className="ds-header__avatar-placeholder" title={user.fullName}>
                  {user.fullName.charAt(0).toUpperCase()}
                </div>
              )}
            </button>
            {userMenuOpen && (
              <div className="ds-header__dropdown">
                <div className="ds-header__dropdown-info">
                  <span className="ds-header__dropdown-name">{user.fullName}</span>
                  <span className="ds-header__dropdown-email">{user.email}</span>
                </div>
                <div className="ds-header__dropdown-divider" />
                <button
                  type="button"
                  className="ds-header__dropdown-item"
                  onClick={() => { setUserMenuOpen(false); onOpenSettings(); }}
                >
                  <SettingsIcon />
                  Settings
                </button>
                <button
                  type="button"
                  className="ds-header__dropdown-item ds-header__dropdown-item--danger"
                  onClick={() => { setUserMenuOpen(false); logout(); }}
                >
                  <LogOutIcon />
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
